import { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  Modal,
  Paper,
  Select,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getInvoices, getInvoiceBranches, fetchFiscalTicket, syncSentInvoicesCache } from '../../services/SalesService';
import { formatInvoiceDate } from '../../utils/DateUtils'; // para dar formato a la fecha
import MonthYearSelector from '../../components/Reports/Sales/MonthYearSelector';
import { getIcon } from '../../utils/IconHandler';
import SnackbarUtils from '../../utils/SnackbarUtils';
import dayjs from 'dayjs';
import { formatMoney } from '../../utils/currency';

/** Mapeo estado CFE -> { labelKey, color, lightGreen } para badges */
const getInvoiceStatusConfig = (status) => {
  const normalized = (status || '').toUpperCase().trim();
  switch (normalized) {
    case 'FACTURA_LISTA_SENT':
      return { label: 'Enviado', color: 'success' };
    case 'FACTURA_LISTA_IN_PROCESS':
      return { label: 'En proceso', color: 'info' };
    case 'FACTURA_LISTA_CANCELLED':
      return { label: 'Anulado', color: 'error' };
    case 'SCHEDULED':
      return { labelKey: 'invoice_status_scheduled', color: 'default' }; // Gris
    case 'SENT':
      return { labelKey: 'invoice_status_sent', color: 'info' }; // Azul
    case 'PROCESSED_ACCEPTED':
      return { labelKey: 'invoice_status_processed_accepted', color: 'success', lightGreen: true }; // Verde claro
    case 'REPORTED_DAILY_REPORT':
      return { labelKey: 'invoice_status_reported_daily_report', color: 'success' }; // Verde
    case 'PROCESSED_REJECTED':
      return { labelKey: 'invoice_status_processed_rejected', color: 'error' }; // Rojo
    case 'CREATED':
      return { labelKey: 'invoice_status_created', darkGray: true }; // Gris oscuro (igual que Otro)
    case 'PE':
      return { label: 'Pendiente de envío a DGI', color: 'warning' };
    case 'AE':
      return { label: 'Aceptado por DGI', color: 'success', lightGreen: true };
    case 'DE':
      return { label: 'Devuelto por DGI', color: 'warning' };
    case 'RE':
      return { label: 'Rechazado por DGI', color: 'error' };
    case 'BE':
      return { label: 'Borrador en espera', darkGray: true };
    case 'CA':
      return { label: 'Anulado', darkGray: true };
    case 'CE':
      return { label: 'Con errores', color: 'error' };
    default:
      return { raw: true, darkGray: true }; // Mostrar enum tal cual, gris oscuro
  }
};

const mapCfeTypeToLabel = (type) => {
  switch (String(type || '')) {
    case '101':
      return 'Ticket';
    case '111':
      return 'Factura';
    case '112':
      return 'NC-Factura';
    case '102':
      return 'NC-Ticket';
    default:
      return `Tipo ${type}`;
  }
};

/** Mismos 4 códigos CFE que mapCfeTypeToLabel, para el filtro de tipo de comprobante. */
const CFE_TYPE_OPTIONS = [
  { value: '101', label: 'Ticket' },
  { value: '111', label: 'Factura' },
  { value: '112', label: 'NC-Factura' },
  { value: '102', label: 'NC-Ticket' },
];

const formatLastUpdated = (value) => {
  if (!value) return 'Sin sincronización registrada';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : value;
};

// `currency` es la moneda del propio comprobante (TpoMoneda); sin dato (comprobantes históricos) se asume pesos.
const formatCurrency = (value, currency) => {
  const number = Number(value);
  return isNaN(number)
    ? '-'
    : formatMoney(number, currency || undefined, { symbol: false });
};

const formatQuantity = (value) => {
  const number = Number(value);
  return isNaN(number)
    ? null
    : new Intl.NumberFormat('es-UY', { maximumFractionDigits: 2 }).format(number);
};

const numberOrNull = (value) => {
  const number = Number(value);
  return isNaN(number) ? null : number;
};

const calculateLineAmount = (item) => {
  if (item?.amount != null) return Number(item.amount);
  const quantity = numberOrNull(item?.quantity);
  const unitPrice = numberOrNull(item?.unitPrice);
  if (quantity == null || unitPrice == null) return null;

  const subtotal = quantity * unitPrice;
  const discountAmount = numberOrNull(item?.discountAmount);
  const discountPercent = numberOrNull(item?.discountPercent);
  const discount = discountAmount != null
    ? discountAmount
    : (discountPercent != null ? subtotal * discountPercent / 100 : 0);

  return subtotal - discount;
};

const calculateUnitDiscount = (item) => {
  const quantity = numberOrNull(item?.quantity);
  const unitPrice = numberOrNull(item?.unitPrice);
  const discountAmount = numberOrNull(item?.discountAmount);
  const discountPercent = numberOrNull(item?.discountPercent);
  if (discountAmount != null && discountAmount !== 0 && quantity) return discountAmount / quantity;
  if (discountPercent != null && discountPercent !== 0 && unitPrice != null) return unitPrice * discountPercent / 100;

  const lineAmount = calculateLineAmount(item);
  if (quantity && unitPrice != null && lineAmount != null) {
    const unitDiscount = unitPrice - (lineAmount / quantity);
    return Math.abs(unitDiscount) > 0.01 ? unitDiscount : null;
  }
  return null;
};

const calculateFinalUnitPrice = (item) => {
  const quantity = numberOrNull(item?.quantity);
  const lineAmount = calculateLineAmount(item);
  if (!quantity || lineAmount == null) return null;
  return lineAmount / quantity;
};

const scoreInvoiceItem = (item) => {
  const unitPrice = numberOrNull(item?.unitPrice);
  const finalUnitPrice = calculateFinalUnitPrice(item);
  const unitDiscount = calculateUnitDiscount(item);
  return [
    unitDiscount != null && unitDiscount !== 0 ? 4 : 0,
    finalUnitPrice != null && unitPrice != null && Math.abs(finalUnitPrice - unitPrice) > 0.01 ? 3 : 0,
    calculateLineAmount(item) != null ? 2 : 0,
    unitPrice != null ? 1 : 0,
  ].reduce((sum, value) => sum + value, 0);
};

const dedupeInvoiceItems = (items = []) => {
  const byLine = new Map();

  items.forEach((item) => {
    const key = [
      (item?.description || '').trim().toLowerCase(),
      numberOrNull(item?.quantity)?.toString() || '',
      numberOrNull(item?.unitPrice)?.toString() || '',
    ].join('|');

    if (!key.trim()) return;

    const current = byLine.get(key);
    if (!current || scoreInvoiceItem(item) > scoreInvoiceItem(current)) {
      byLine.set(key, item);
    }
  });

  return Array.from(byLine.values());
};

const SentInvoicesTable = ({ selectedRut, provider = null, embedded = false }) => {
  const { t } = useTranslation();
  const normalizedProvider = (provider || '').toUpperCase();
  const isFacturaListaProvider = normalizedProvider === 'FACTURA_LISTA';

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [month, setMonth] = useState(dayjs().month() + 1);
  const [year, setYear] = useState(dayjs().year());
  const [totalCount, setTotalCount] = useState(0);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPdfId, setLoadingPdfId] = useState(null);
  const [liveMode, setLiveMode] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [dataSource, setDataSource] = useState('cache');
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranchIds, setSelectedBranchIds] = useState([]);
  const [selectedCfeTypes, setSelectedCfeTypes] = useState([]);
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);
  const filtersDisabled = isFacturaListaProvider && liveMode;

  // Sucursales configuradas bajo este RUT, para el filtro de sucursal (solo se muestra si hay
  // más de una). Se resetea la selección al cambiar de RUT para no arrastrar ids de otro RUT.
  useEffect(() => {
    if (selectedRut === null) {
      setBranches([]);
      return;
    }
    setSelectedBranchIds([]);
    getInvoiceBranches(selectedRut).then(setBranches);
  }, [selectedRut]);

  // Debounce para el término de búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Resetear a la primera página cuando cambie la búsqueda
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (selectedRut === null) {
      return;
    }
    
    const fetch = async () => {
      setLoading(true);
      try {
        const data = await getInvoices({
          month,
          year,
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          search: debouncedSearchTerm,
          rut: selectedRut,
          live: isFacturaListaProvider && liveMode,
          // Los controles de sucursal/tipo de CFE se deshabilitan en modo tiempo real (el usuario
          // no puede tocarlos), pero si quedó una selección previa igual se mandaba al backend y
          // el listado en vivo seguía filtrado sin que la UI lo mostrara -- se omiten acá.
          locationIds: filtersDisabled ? [] : selectedBranchIds,
          cfeTypes: filtersDisabled ? [] : selectedCfeTypes
        });
        
        // Asegurar que data tenga la estructura esperada
        const invoices = data?.invoices || [];
        const totalCount = data?.totalCount || 0;
        
        setInvoices(invoices);
        setTotalCount(totalCount);
        setDataSource(data?.source || (liveMode ? 'live' : 'cache'));
        setLastUpdatedAt(data?.lastUpdatedAt || null);
      } catch (e) {
        console.error('Error fetching invoices', e);
        setInvoices([]);
        setTotalCount(0);
        setDataSource('error');
        setLastUpdatedAt(null);
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [month, year, paginationModel.page, paginationModel.pageSize, debouncedSearchTerm, selectedRut, liveMode, refreshTick, isFacturaListaProvider, selectedBranchIds, selectedCfeTypes]);

  useEffect(() => {
    if (!isFacturaListaProvider && liveMode) {
      setLiveMode(false);
    }
  }, [isFacturaListaProvider, liveMode]);

  const handleToggleLiveMode = () => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    setLiveMode(prev => !prev);
  };

  const handleBranchFilterChange = (event) => {
    const { value } = event.target;
    setSelectedBranchIds(typeof value === 'string' ? value.split(',') : value);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleCfeTypeFilterChange = (event) => {
    const { value } = event.target;
    setSelectedCfeTypes(typeof value === 'string' ? value.split(',') : value);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleSyncCache = async () => {
    try {
      setSyncing(true);
      await syncSentInvoicesCache();
      SnackbarUtils.success('Facturas sincronizadas');
      setLiveMode(false);
      setPaginationModel(prev => ({ ...prev, page: 0 }));
      setRefreshTick(prev => prev + 1);
    } catch (error) {
      console.error('Error sincronizando facturas emitidas:', error);
      SnackbarUtils.error('No se pudo sincronizar FacturaLista');
    } finally {
      setSyncing(false);
    }
  };

  // El backend (SalesController /invoice/ticket/from_external_document_id) resuelve el PDF con el
  // proveedor que efectivamente emitió esa factura.
  const handleViewPDF = async (externalDocumentId) => {
    try {
      setLoadingPdfId(externalDocumentId);
      const pdfBlob = await fetchFiscalTicket(externalDocumentId, selectedRut ?? undefined);
      if (pdfBlob) {
        const url = URL.createObjectURL(pdfBlob);
        setPdfUrl(url);
        setPdfModalOpen(true);
      } else {
        alert('No se pudo cargar el PDF de la factura. Probá de nuevo en unos minutos.');
      }
    } catch (error) {
      console.error('Error al obtener el PDF de la factura:', error);
      alert('Error al cargar el PDF de la factura');
    } finally {
      setLoadingPdfId(null);
    }
  };

  // Función para cerrar el modal y limpiar la URL
  const handleClosePdfModal = () => {
    setPdfModalOpen(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  // El filtrado ahora se hace en el servidor, no necesitamos filteredInvoices  

  const columns = [
    { field: 'id', headerName: 'ID', flex: 1.3 },
    { field: 'date', align: 'center', headerAlign: 'center', headerName: 'Fecha', flex: 0.8, valueFormatter: (params) => formatInvoiceDate(params) },
    { field: 'serie', align: 'center', headerAlign: 'center', headerName: 'Serie', flex: 0.5 },
    { field: 'nro', align: 'center', headerAlign: 'center', headerName: 'Número', flex: 0.5 },
    {
      field: 'type',
      headerName: 'Tipo',
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (params) => mapCfeTypeToLabel(params),
    },
    { field: 'client', align: 'center', headerAlign: 'center', headerName: 'Cliente', flex: 1 },
    {
      field: 'status',
      headerName: 'Estado',
      flex: 1.3,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const config = getInvoiceStatusConfig(params.value);
        const label = config.label ?? (config.raw ? params.value : t(config.labelKey));
        const darkGraySx = config.darkGray
          ? { fontWeight: 500, bgcolor: 'grey.600', color: 'grey.100' }
          : { fontWeight: 500 };
        const lightGreenSx = config.lightGreen
          ? { fontWeight: 500, bgcolor: '#81c784', color: 'white' }
          : undefined;
        const chipSx = config.darkGray ? darkGraySx : (config.lightGreen ? lightGreenSx : { fontWeight: 500 });
        const pendingToRemove = params.row.pendingToRemove === true;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" flexWrap="wrap">
              <Chip
                label={label}
                color={config.darkGray ? 'default' : config.color}
                size="small"
                sx={chipSx}
              />
              {pendingToRemove && (
                <Chip
                  label={t('invoice_pending_credit_note')}
                  size="small"
                  sx={{ fontWeight: 500, bgcolor: '#ed6c02', color: 'white', '&:hover': { bgcolor: '#e65100' } }}
                />
              )}
            </Stack>
          </Box>
        );
      },
    },
    {
      field: 'currency',
      headerName: 'Moneda',
      flex: 0.5,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value) => value || 'UYU',
    },
    {
      field: 'total',
      headerName: 'Total',
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value, row) => {
        const number = parseFloat(value);
        return isNaN(number) ? '—' : formatMoney(number, row?.currency || undefined, { symbol: false });
      },
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const isLoading = loadingPdfId === params.row.id;
        return (
          <IconButton 
            onClick={() => setSelectedInvoice(params.row)}
            title="Ver detalle"
            disabled={isLoading}
          >
            {getIcon('eye')}
          </IconButton>
        );
      },
    }
  ];

  const Root = embedded ? Box : Paper;
  const rootProps = embedded
    ? { sx: { mb: 2 } }
    : { sx: { padding: 2, mb: 2 } };

  return (
    <Root {...rootProps}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label={t('search')}
            placeholder="Cliente, serie, número o producto"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: '100%', sm: 260 } }}
          />
          {branches.length > 1 && (
            <Tooltip title={filtersDisabled ? 'No disponible en modo tiempo real' : ''}>
              <FormControl size="small" sx={{ width: { xs: '100%', sm: 200 } }} disabled={filtersDisabled}>
                <InputLabel shrink>Sucursal</InputLabel>
                <Select
                  label="Sucursal"
                  notched
                  displayEmpty
                  multiple
                  value={selectedBranchIds}
                  onChange={handleBranchFilterChange}
                  renderValue={(selected) => selected.length === 0
                    ? 'Todas las sucursales'
                    : selected.map((id) => branches.find((b) => String(b.locationId) === String(id))?.name || id).join(', ')}
                >
                  {branches.map((branch) => (
                    <MenuItem key={branch.locationId} value={String(branch.locationId)}>
                      <Checkbox checked={selectedBranchIds.includes(String(branch.locationId))} />
                      <ListItemText primary={branch.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Tooltip>
          )}
          <Tooltip title={filtersDisabled ? 'No disponible en modo tiempo real' : ''}>
            <FormControl size="small" sx={{ width: { xs: '100%', sm: 200 } }} disabled={filtersDisabled}>
              <InputLabel shrink>Tipo de comprobante</InputLabel>
              <Select
                label="Tipo de comprobante"
                notched
                displayEmpty
                multiple
                value={selectedCfeTypes}
                onChange={handleCfeTypeFilterChange}
                renderValue={(selected) => selected.length === 0
                  ? 'Todos los tipos'
                  : selected.map((value) => CFE_TYPE_OPTIONS.find((o) => o.value === value)?.label || value).join(', ')}
              >
                {CFE_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox checked={selectedCfeTypes.includes(option.value)} />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Tooltip>
          <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
          <Box sx={{ width: { xs: '100%', sm: 220 } }}>
            <MonthYearSelector month={month} year={year} setMonth={setMonth} setYear={setYear} />
          </Box>
          {isFacturaListaProvider && (
            <>
              <Tooltip title="Más opciones">
                <IconButton
                  size="small"
                  onClick={(e) => setActionsMenuAnchor(e.currentTarget)}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                >
                  {getIcon('moreVertical')}
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={actionsMenuAnchor}
                open={Boolean(actionsMenuAnchor)}
                onClose={() => setActionsMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  onClick={() => { handleToggleLiveMode(); setActionsMenuAnchor(null); }}
                  disabled={loading || syncing}
                >
                  <ListItemIcon>{getIcon(liveMode ? 'flash' : 'eye')}</ListItemIcon>
                  <ListItemText
                    primary={liveMode ? 'Desactivar tiempo real' : 'Activar tiempo real'}
                    secondary="Consulta directamente a FacturaLista para este mes y RUT. Puede demorar más."
                  />
                </MenuItem>
                <MenuItem
                  onClick={() => { handleSyncCache(); setActionsMenuAnchor(null); }}
                  disabled={loading || syncing || liveMode}
                >
                  <ListItemIcon>{syncing ? <CircularProgress size={16} /> : getIcon('refresh')}</ListItemIcon>
                  <ListItemText
                    primary="Sincronizar"
                    secondary="Actualiza la cache local de facturas emitidas desde FacturaLista."
                  />
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Box>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
        <Chip
          size="small"
          label={
            isFacturaListaProvider
              ? (liveMode || dataSource === 'live' ? 'Origen: Tiempo real' : 'Origen: Local')
              : 'Origen: Tiempo real'
          }
          color={isFacturaListaProvider && (liveMode || dataSource === 'live') ? 'info' : 'default'}
          variant={isFacturaListaProvider && (liveMode || dataSource === 'live') ? 'filled' : 'outlined'}
        />
        {isFacturaListaProvider && !liveMode && dataSource !== 'live' && (
          <Typography variant="caption" color="text.secondary">
            Última actualización: {formatLastUpdated(lastUpdatedAt)}
          </Typography>
        )}
      </Stack>

      <DataGrid
        loading={loading}
        rows={invoices}
        columns={columns}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        rowCount={totalCount}
        getRowId={(row) => row.id}
        autoHeight
      />

      <SentInvoiceDetailDialog
        invoice={selectedInvoice}
        onClose={() => setSelectedInvoice(null)}
        onViewTicket={handleViewPDF}
        loadingPdfId={loadingPdfId}
      />

      {/* Modal para visualizar PDF */}
      <Modal
        open={pdfModalOpen}
        onClose={handleClosePdfModal}
        aria-labelledby="pdf-modal-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: '90%',
            height: '90%',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography id="pdf-modal-title" variant="h6" component="h2">
              Visualizador de PDF
            </Typography>
            <IconButton onClick={handleClosePdfModal} size="small">
              {getIcon('close')}
            </IconButton>
          </Box>
          
          <Box sx={{ flex: 1, minHeight: 0 }}>
            {pdfUrl && (
              <iframe
                src={pdfUrl}
                width="100%"
                height="100%"
                style={{ border: 'none', borderRadius: '4px' }}
                title="PDF Viewer"
              />
            )}
          </Box>
        </Box>
      </Modal>
    </Root>
  );
};

const SentInvoiceDetailDialog = ({ invoice, onClose, onViewTicket, loadingPdfId }) => {
  const items = dedupeInvoiceItems(invoice?.items || []);
  const isLoadingTicket = invoice?.id && loadingPdfId === invoice.id;
  const total = invoice?.total ?? invoice?.montoTotal;
  const currency = invoice?.currency;
  const isForeign = Boolean(currency) && currency !== 'UYU';

  return (
    <Dialog open={Boolean(invoice)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Detalle de factura emitida</DialogTitle>
      <DialogContent dividers>
        {invoice && (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1.4fr 1fr 1fr' },
                gap: 2,
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'grey.50',
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">Cliente</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{invoice.client || 'Consumidor final'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Comprobante</Typography>
                <Typography variant="body2">{mapCfeTypeToLabel(invoice.type)} {invoice.serie}-{invoice.nro}</Typography>
                <Typography variant="body2" color="text.secondary">{formatInvoiceDate(invoice.date)}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Moneda: {currency || 'UYU'}
                  {isForeign && invoice.exchangeRate ? ` · T/C ${Number(invoice.exchangeRate).toLocaleString('es-UY', { maximumFractionDigits: 3 })}` : ''}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Totales</Typography>
                <Typography variant="body2">IVA: {formatCurrency(invoice.iva, currency)}</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Total: {formatCurrency(total, currency)}</Typography>
              </Box>
            </Box>

            {items.length ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 440 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Producto / servicio</TableCell>
                      <TableCell align="right">Cantidad</TableCell>
                      <TableCell align="right">Precio unitario</TableCell>
                      <TableCell align="right">Descuento unit.</TableCell>
                      <TableCell align="right">Precio final unit.</TableCell>
                      <TableCell align="right">Importe</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, index) => (
                      <TableRow key={`${item.description}-${index}`}>
                        <TableCell sx={{ fontWeight: 500 }}>{item.description || '-'}</TableCell>
                        <TableCell align="right">{formatQuantity(item.quantity) || '-'}</TableCell>
                        <TableCell align="right">{item.unitPrice != null ? formatCurrency(item.unitPrice, currency) : '-'}</TableCell>
                        <TableCell align="right">{calculateUnitDiscount(item) != null ? formatCurrency(calculateUnitDiscount(item), currency) : '-'}</TableCell>
                        <TableCell align="right">{calculateFinalUnitPrice(item) != null ? formatCurrency(calculateFinalUnitPrice(item), currency) : '-'}</TableCell>
                        <TableCell align="right">{calculateLineAmount(item) != null ? formatCurrency(calculateLineAmount(item), currency) : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary">
                No hay detalle de ítems guardado para este comprobante. Podés ver el ticket original.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {invoice?.id && (
          <Button
            onClick={() => onViewTicket(invoice.id)}
            startIcon={isLoadingTicket ? <CircularProgress size={16} /> : getIcon('invoice')}
            disabled={Boolean(isLoadingTicket)}
          >
            Ver ticket
          </Button>
        )}
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SentInvoicesTable;
